/**
 * DS3-0414: does the transaction already carry a REAL signer signature? A Solana
 * signature is computed over the compiled message, which INCLUDES recentBlockhash, so
 * the blockhash must not be rewritten once any signer (sponsor / multisig / co-signer)
 * has signed - a rewrite silently invalidates their signature. Legacy signatures are
 * `{ signature: Buffer | null }` entries; versioned signatures are 64-byte arrays that
 * stay all-zero placeholders until a signer fills them. Mirrors turnkey-signer-bridge.ts.
 */
export function transactionHasSignature(transaction) {
    if ('serializeMessage' in transaction) {
        return transaction.signatures.some((s) => s.signature != null);
    }
    return transaction.signatures.some((sig) => sig.some((b) => b !== 0));
}
/**
 * Extracts a human-readable error message from Solana transaction logs.
 */
export function extractTransactionError(err, logMessages) {
    if (logMessages) {
        return JSON.stringify(logMessages);
    }
    if (err) {
        try {
            return JSON.stringify(err);
        }
        catch (_a) {
            return String(err);
        }
    }
    return 'Unknown transaction error';
}
/** The highest Solana transaction version this SDK reads back (SIMD-0385 v1). */
export const SOLANA_MAX_SUPPORTED_TRANSACTION_VERSION = 1;
/**
 * getParsedTransaction that survives a SIMD-0385 v1 transaction. @solana/web3.js
 * 1.98.x validates the response `version` against `0 | 'legacy'` and throws on 1
 * even when the request raised the ceiling, so when web3.js refuses to parse and
 * the connection exposes its endpoint, the same jsonParsed result is read as raw
 * JSON-RPC. Legacy and v0 responses keep coming back as web3.js parsed objects.
 */
export async function getParsedTransactionCompat(connection, signature) {
    var _a;
    try {
        return await connection.getParsedTransaction(signature, {
            maxSupportedTransactionVersion: SOLANA_MAX_SUPPORTED_TRANSACTION_VERSION,
            commitment: 'confirmed',
        });
    }
    catch (error) {
        const endpoint = typeof (connection === null || connection === void 0 ? void 0 : connection.rpcEndpoint) === 'string' ? connection.rpcEndpoint.trim() : '';
        if (!endpoint)
            throw error;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                jsonrpc: '2.0',
                id: 1,
                method: 'getTransaction',
                params: [signature, {
                        encoding: 'jsonParsed',
                        commitment: 'confirmed',
                        maxSupportedTransactionVersion: SOLANA_MAX_SUPPORTED_TRANSACTION_VERSION,
                    }],
            }),
        });
        if (!response.ok)
            throw error;
        const body = await response.json();
        if (body === null || body === void 0 ? void 0 : body.error)
            throw error;
        return (_a = body === null || body === void 0 ? void 0 : body.result) !== null && _a !== void 0 ? _a : null;
    }
}
/**
 * Polls getSignatureStatuses until the transaction is confirmed, then checks for errors.
 * Throws if the transaction failed on-chain. Returns parsed transaction info on success.
 */
export async function confirmAndCheckTransaction(connection, signature) {
    var _a;
    const maxAttempts = 15;
    for (let i = 0; i < maxAttempts; i++) {
        const { value } = await connection.getSignatureStatuses([signature]);
        const status = value[0];
        if (status) {
            if (status.err) {
                let logMessages;
                try {
                    const txInfo = await getParsedTransactionCompat(connection, signature);
                    logMessages = (_a = txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta) === null || _a === void 0 ? void 0 : _a.logMessages;
                }
                catch (_b) {
                    // Log retrieval is diagnostic only; the on-chain error is the verdict.
                }
                const errorMessage = extractTransactionError(status.err, logMessages);
                throw new Error(`Transaction failed: ${errorMessage}`);
            }
            if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') {
                return await getParsedTransactionCompat(connection, signature);
            }
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
    throw new Error('Transaction confirmation timeout');
}
